import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrackedLink } from "@/components/analytics/TrackedLink";
import { trackCta, ctaIdFromPath } from "@/lib/analytics/cta";
import FivestarLogo from "./FivestarLogo";
import LoginMenu from "./LoginMenu";
import MobileMenu from "./MobileMenu";
import MenuToggle from "./MenuToggle";
import GetTheAppModal from "./GetTheAppModal";
import { isAppContext } from "@/lib/appContext";

interface SiteHeaderProps {
  /**
   * Dark-surface variant: a translucent black bar with the white logo, nav text,
   * Login chooser and menu toggle, for pages whose leading section is dark (e.g.
   * the creator page). Defaults to the light treatment used on the homepage.
   */
  inverse?: boolean;
  /** Secondary nav link label + destination (defaults to the creator page). */
  navLabel?: string;
  navTo?: string;
  /** Show the "Become a Member" link before the secondary nav link (homepage
   *  default). Lead pages that already point their nav at members pass false. */
  hasBecomeAMemberLink?: boolean;
  /** Primary CTA label + the id of the in-page section it smooth-scrolls to.
   *  When ctaLabel is OMITTED the header renders the default "Get the App"
   *  CTA, which opens the store-badges overlay instead of scrolling. */
  ctaLabel?: string;
  ctaTargetId?: string;
  /**
   * When set, the bar is transparent at the very top (over a full-bleed hero)
   * and only paints its background + blur once the user begins to scroll.
   * Without it the header always carries its background (the homepage default).
   */
  isTransparentAtTop?: boolean;
}

/**
 * Fixed top header for the marketing site. Translucent with a backdrop blur over
 * scrolled content — white over light pages, black (`inverse`) over dark pages.
 * Desktop shows a secondary nav link, the primary CTA and a Login chooser
 * (Members/Creators). Mobile shows the CTA and a hamburger that morphs into an X
 * and opens the full-screen menu; while open the header goes transparent and
 * rises above the overlay so the morphing toggle stays visible and acts as the
 * close.
 *
 * The nav link and CTA are configurable so non-homepage surfaces (e.g. the
 * creator page) can point them at their own destination and in-page target.
 */
const SiteHeader = ({
  inverse = false,
  navLabel = "Become a Creator",
  navTo = "/become-a-creator",
  hasBecomeAMemberLink = true,
  ctaLabel,
  ctaTargetId = "lead-form",
  isTransparentAtTop = false,
}: SiteHeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [appModalOpen, setAppModalOpen] = useState(false);
  // No explicit ctaLabel -> the launch-window default: "Get the App" opening
  // the store-badges overlay (Bill, 8/31). Pages that pass ctaLabel keep
  // their scroll-to-section CTA (e.g. the creator pages' "Apply").
  const useAppCta = ctaLabel === undefined;
  const appContext = isAppContext();

  const openAppModal = (location: string) => {
    trackCta({ cta_id: "get_the_app", location, destination: "#get-the-app-modal" });
    setMenuOpen(false);
    setAppModalOpen(true);
  };

  // When `isTransparentAtTop` is set, the bar is transparent at the top and only
  // gains its background once the user scrolls past a few pixels.
  useEffect(() => {
    if (!isTransparentAtTop) return;
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll(); // sync now in case we land already scrolled (e.g. hash nav)
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isTransparentAtTop]);

  const handleCtaClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackCta({ cta_id: "join", location: "header", destination: `#${ctaTargetId}` });

    const target = document.getElementById(ctaTargetId);
    if (!target) return;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  // On a dark (inverse) header the logo, nav text, Login chooser and toggle all
  // read white; while the mobile menu is open everything is white over the
  // overlay regardless of variant.
  const onDark = inverse || menuOpen;
  const closedBg = inverse ? "bg-black/70" : "bg-white/70";
  // Pages without `backgroundOnScroll` always carry the bar background; with it,
  // the bar stays transparent at the top and only paints once scrolled.
  const showBg = !isTransparentAtTop || scrolled;

  return (
    <header
      className={`fixed top-0 inset-x-0 transition-colors duration-200 ${
        menuOpen
          ? "z-[70] bg-transparent"
          : `z-40 ${showBg ? `${closedBg} backdrop-blur-md` : "bg-transparent"}`
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" aria-label="Fivestar home" className="flex items-center">
          <FivestarLogo
            variant="horizontal"
            className={`h-7 w-auto transition-colors ${onDark ? "text-white" : "text-black"}`}
            fill="currentColor"
          />
        </Link>

        <nav className="flex items-center gap-4 sm:gap-6">
          {hasBecomeAMemberLink && (
            <TrackedLink
              ctaId="become_a_member"
              ctaLocation="header"
              to="/become-a-member"
              className={`hidden md:inline-flex text-sm font-semibold ${
                inverse ? "text-white" : "text-black"
              } hover:opacity-80 transition-opacity duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2 rounded`}
            >
              Become a Member
            </TrackedLink>
          )}
          <TrackedLink
            ctaId={ctaIdFromPath(navTo)}
            ctaLocation="header"
            to={navTo}
            className={`hidden md:inline-flex text-sm font-semibold ${
              inverse ? "text-white" : "text-black"
            } hover:opacity-80 transition-opacity duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2 rounded`}
          >
            {navLabel}
          </TrackedLink>

          {useAppCta ? (
            !appContext && <button
              type="button"
              onClick={() => openAppModal("header")}
              className={`inline-flex items-center bg-cyan hover:bg-cyan/90 text-black font-semibold tracking-wide px-5 py-2 rounded-full text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 ${
                menuOpen ? "max-md:hidden" : ""
              }`}
            >
              Get the App
            </button>
          ) : (
            <a
              href={`#${ctaTargetId}`}
              onClick={handleCtaClick}
              className={`inline-flex items-center bg-cyan hover:bg-cyan/90 text-black font-semibold tracking-wide px-5 py-2 rounded-full text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 ${
                menuOpen ? "max-md:hidden" : ""
              }`}
            >
              {ctaLabel}
            </a>
          )}

          {/* Desktop: Login chooser dropdown */}
          <div className="hidden md:block">
            <LoginMenu inverse={inverse} />
          </div>

          {/* Mobile: morphing hamburger/X that toggles the full-screen menu */}
          <MenuToggle
            isOpen={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className={`md:hidden ${onDark ? "text-white" : "text-black"}`}
          />
        </nav>
      </div>

      <MobileMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onGetTheApp={useAppCta && !appContext ? () => openAppModal("mobile_menu") : undefined}
      />
      {!appContext && <GetTheAppModal isOpen={appModalOpen} onClose={() => setAppModalOpen(false)} />}
    </header>
  );
};

export default SiteHeader;
